import React, { memo } from "react";
import { TextArea } from "components/common/textArea/TextArea";
import { Controller, useFormContext } from "react-hook-form";
import { FieldGridProps } from "./FieldGrid.types";
import { InputV3 } from "components/common/input/InputV3";

const FieldGridV3 = memo(
  <T extends Record<string, any>>({ configs, item: _item, className }: FieldGridProps<T>) => {
    const { control } = useFormContext();

    // Render fields directly without memoization to avoid infinite re-renders
    const renderedFields = configs.map((cfg) => {
      const key = cfg.key;

      // Always in edit mode: use custom component or fallback to Input/TextArea
      if (cfg.EditComponent) {
        return <React.Fragment key={key}>{cfg.EditComponent}</React.Fragment>;
      }

      // Use TextArea for textarea type
      if (cfg.type === "textarea") {
        return (
          <div className="mb-2" key={key}>
            <Controller
              name={cfg.name!}
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextArea
                  {...field}
                  disabled={false}
                  rows={4}
                  label={cfg.label} // Pass label to TextArea
                  placeholder={`Enter ${cfg.label.toLowerCase()}`}
                  alert={error ? { type: "error", message: error.message || "" } : undefined}
                  maxLength={cfg.maxLength}
                />
              )}
            />
          </div>
        );
      }

      // All other types: use InputV3 wrapped in Controller
      return (
        <div className="mb-2" key={key}>
          <Controller
            name={cfg.name!}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <InputV3
                {...field}
                label={cfg.label}
                type={cfg.type!}
                disabled={false}
                error={!!error}
                errorMessage={error?.message}
                placeholder={`Enter ${cfg.label.toLowerCase()}`}
              />
            )}
          />
        </div>
      );
    });

    return <div className={`grid ${className ?? "grid-cols-2"} gap-4`}>{renderedFields}</div>;
  }
) as <T extends Record<string, any>>(props: FieldGridProps<T>) => React.ReactElement;

(FieldGridV3 as any).displayName = "FieldGridV3";

export default FieldGridV3;
